import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Switch,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stack,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import CustomModal from "../../Comman/Modal";
import TableCompo from "../Table/TableCompo";
import { RiDeleteBin6Line } from "react-icons/ri";
import axios from "axios";
import moment from "moment/moment";
import { canShowPermission } from "../Permissions/permissionObject";
import ConfirmationDialog from "../../Modals/ConfirmationModal";
import CreateInterestingFact from "./CreateInterestingFact";

const gradientBtn = {
  textTransform: "none",
  borderRadius: "10px",
  background: "linear-gradient(135deg, #752ADA 0%, #9b4dff 100%)",
  boxShadow: "0 4px 14px rgba(117, 42, 218, 0.28)",
  "&:hover": {
    background: "linear-gradient(135deg, #5f1fb8 0%, #7a3dd4 100%)",
  },
};

const BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api/v1/interesting-facts`;

export default function InterestingFacts() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [facts, setFacts] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sort, setSort] = useState("displayOrder:asc");
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [statusLoadingId, setStatusLoadingId] = useState(null);

  useEffect(() => {
    const storedPermissions = localStorage.getItem("userPermissions");
    if (storedPermissions) {
      try {
        setPermissions(JSON.parse(storedPermissions));
      } catch (error) {
        console.error("Error parsing stored permissions", error);
      }
    }
  }, []);

  const canCreate = canShowPermission(permissions, "interestingFacts", "create");
  const canEdit = canShowPermission(permissions, "interestingFacts", "edit");
  const canDelete = canShowPermission(permissions, "interestingFacts", "delete");

  async function getFacts() {
    setLoading(true);
    try {
      const response = await axios.get(BASE_URL, {
        params: {
          page: page + 1,
          limit,
          search,
          status: statusFilter,
          sort,
        },
      });

      if (response.data.success) {
        setFacts(response.data.data?.facts || []);
        setTotal(response.data.data?.total || 0);
      } else {
        alert(response.data.message || "Failed to fetch interesting facts");
        setFacts([]);
        setTotal(0);
      }
    } catch (error) {
      console.error("Error fetching interesting facts:", error);
      alert(error.response?.data?.message || "Failed to fetch interesting facts");
      setFacts([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    getFacts();
  }, [page, limit, search, statusFilter, sort, open]);

  async function handleToggleStatus(row) {
    if (!canEdit) return;
    setStatusLoadingId(row._id);
    const previous = row.isActive;
    setFacts((prev) =>
      prev.map((item) =>
        item._id === row._id ? { ...item, isActive: !item.isActive } : item,
      ),
    );
    try {
      const response = await axios.patch(`${BASE_URL}/${row._id}/status`, {
        isActive: !previous,
      });
      if (!response.data.success) {
        throw new Error(response.data.message || "Failed to update status");
      }
    } catch (error) {
      setFacts((prev) =>
        prev.map((item) =>
          item._id === row._id ? { ...item, isActive: previous } : item,
        ),
      );
      alert(error.response?.data?.message || error.message || "Failed to update status");
    } finally {
      setStatusLoadingId(null);
    }
  }

  async function handleDeleteConfirm() {
    const id = deleteDialog.id;
    const response = await axios.delete(`${BASE_URL}/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to delete");
    }
    await getFacts();
  }

  const handleTableChange = (type, payload) => {
    if (type === "changePage") setPage(payload.page);
    if (type === "changeRowsPerPage") {
      setLimit(payload.rowPerPage);
      setPage(0);
    }
    if (type === "search") {
      setSearch(payload.searchText || "");
      setPage(0);
    }
  };

  const columns = [
    {
      field: "image",
      headerName: "Image",
      width: isMobile ? 90 : 110,
      render: (_, row) =>
        row.image ? (
          <Button
            size="small"
            startIcon={<ImageOutlinedIcon />}
            onClick={() => window.open(row.image, "_blank")}
            sx={{ textTransform: "none" }}
          >
            View
          </Button>
        ) : (
          "—"
        ),
    },
    {
      field: "title",
      headerName: "Title",
      width: isMobile ? 140 : 220,
      render: (_, row) => (
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{
            maxWidth: isMobile ? 140 : 220,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {row.title || "N/A"}
        </Typography>
      ),
    },
    {
      field: "isActive",
      headerName: "Status",
      width: 120,
      render: (_, row) => (
        <Switch
          checked={!!row.isActive}
          disabled={!canEdit || statusLoadingId === row._id}
          onChange={() => handleToggleStatus(row)}
          color="primary"
        />
      ),
    },
    {
      field: "displayOrder",
      headerName: "Display Order",
      width: 130,
      render: (_, row) => row.displayOrder ?? 0,
    },
    {
      field: "createdAt",
      headerName: "Created Date",
      width: isMobile ? 130 : 170,
      render: (_, row) =>
        row.createdAt ? moment(row.createdAt).format("DD MMM YYYY, hh:mm A") : "—",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      render: (_, row) => (
        <Stack direction="row" spacing={1}>
          {canEdit && (
            <EditOutlinedIcon
              style={{ cursor: "pointer", color: "#752ADA" }}
              onClick={() => {
                setSelectedRow(row);
                setOpen(true);
              }}
            />
          )}
          {canDelete && (
            <RiDeleteBin6Line
              style={{ cursor: "pointer", color: "#d32f2f", fontSize: 20 }}
              onClick={() => setDeleteDialog({ open: true, id: row._id })}
            />
          )}
        </Stack>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%" }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Typography variant="h6" fontWeight={700}>
          Interesting Facts
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Active</MenuItem>
              <MenuItem value="false">Inactive</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Sort</InputLabel>
            <Select
              label="Sort"
              value={sort}
              onChange={(e) => {
                setSort(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="displayOrder:asc">Display Order ↑</MenuItem>
              <MenuItem value="displayOrder:desc">Display Order ↓</MenuItem>
              <MenuItem value="createdAt:desc">Newest First</MenuItem>
              <MenuItem value="createdAt:asc">Oldest First</MenuItem>
              <MenuItem value="title:asc">Title A-Z</MenuItem>
            </Select>
          </FormControl>
          {canCreate && (
            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              sx={gradientBtn}
              onClick={() => {
                setSelectedRow(null);
                setOpen(true);
              }}
            >
              Add Fact
            </Button>
          )}
        </Stack>
      </Stack>

      <TableCompo
        columns={columns}
        data={facts}
        page={page}
        limit={limit}
        totalCount={total || 0}
        onTableChange={handleTableChange}
        loading={loading}
      />

      <CustomModal
        open={open}
        setOpen={setOpen}
        content={
          <CreateInterestingFact
            header={selectedRow ? "Edit Interesting Fact" : "Add Interesting Fact"}
            button_name={selectedRow ? "Update" : "Save"}
            row={selectedRow}
            onSave={() => {
              setOpen(false);
              setSelectedRow(null);
              getFacts();
            }}
            onCancel={() => {
              setOpen(false);
              setSelectedRow(null);
            }}
          />
        }
      />

      <ConfirmationDialog
        open={deleteDialog.open}
        title="Delete Interesting Fact?"
        content="Are you sure you want to delete this interesting fact? This action cannot be undone."
        onClose={(success) => {
          setDeleteDialog({ open: false, id: null });
          if (success) getFacts();
        }}
        onConfirm={handleDeleteConfirm}
        successMessage="Interesting fact deleted successfully"
        errorMessage="Failed to delete interesting fact"
      />
    </Box>
  );
}
